import React from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, LifeBuoy, ArrowRight, ArrowLeft, Bot, ShieldCheck, Radar, Scale } from 'lucide-react';
import { useRecoveryPortal } from '../context/RecoveryPortalContext';

const LOGO_URL = '/logo.jpg';

const PortalCard = ({ title, subtitle, bullets, icon: Icon, accent, onClick, testId, delay, label }) => (
    <motion.button
        type="button"
        onClick={onClick}
        data-testid={testId}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.5 }}
        whileHover={{ y: -6 }}
        className="group relative flex-1 text-left p-6 md:p-8 bg-[#0a0a0a]/90 border rounded-2xl overflow-hidden focus:outline-none"
        style={{ borderColor: `${accent}40` }}
    >
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ background: accent }} />
        <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 rounded-tl-2xl" style={{ borderColor: accent }} />
        <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 rounded-br-2xl" style={{ borderColor: accent }} />
        <span className="inline-block text-[10px] uppercase tracking-[0.3em] mb-5 px-2 py-1 rounded" style={{ color: accent, background: `${accent}15` }}>{label}</span>
        <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-xl" style={{ background: `${accent}20` }}>
                <Icon size={28} style={{ color: accent }} />
            </div>
            <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white font-rajdhani uppercase tracking-wide">{title}</h2>
                <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
            </div>
        </div>
        <ul className="space-y-2 mt-6 mb-8">
            {bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <b.icon size={14} style={{ color: accent }} /> {b.text}
                </li>
            ))}
        </ul>
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ color: accent }}>
            Enter portal <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </div>
    </motion.button>
);

export default function PortalChooserPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const mode = params.get('mode') === 'login' ? 'login' : 'register';
    const { config } = useRecoveryPortal();
    const accent = config.accent_color;

    const tradingPath = mode === 'login' ? '/login' : '/register';
    const recoveryPath = mode === 'login' ? '/recovery/login' : '/recovery/register';

    return (
        <div className="min-h-screen bg-[#050505] relative overflow-x-hidden flex flex-col" data-testid="portal-chooser">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(91,164,164,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(91,164,164,0.4) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
                <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] rounded-full bg-[#5BA4A4]/10 blur-[140px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full blur-[140px]" style={{ background: `${accent}18` }} />
            </div>

            <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
                <Link to="/" className="flex items-center gap-3" data-testid="portal-home-link">
                    <img src={LOGO_URL} alt="The Cybernetics" className="w-10 h-10 rounded-full border border-[#5BA4A4]/40" />
                    <span className="font-rajdhani font-bold uppercase tracking-widest text-[#5BA4A4]">The Cybernetics</span>
                </Link>
                <Link to="/" className="text-xs text-gray-500 hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
            </header>

            <main className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-12 pb-16 max-w-6xl mx-auto w-full">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 md:mb-14 max-w-2xl">
                    <p className="text-[11px] uppercase tracking-[0.4em] text-gray-500 mb-3">{mode === 'login' ? 'Sign in' : 'Get started'}</p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-rajdhani text-white leading-[1.05]">
                        Choose your <span className="text-[#5BA4A4]">portal</span>
                    </h1>
                    <p className="text-base md:text-lg text-gray-400 mt-4">Two dedicated wallets, one platform. Trade with AI-driven tools, or work with our recovery desk to reclaim lost digital assets.</p>
                </motion.div>

                <div className="flex flex-col lg:flex-row gap-5 md:gap-8">
                    <PortalCard
                        label="Portal 01"
                        title="Trading Wallet"
                        subtitle="AI trading, live markets, deposits & withdrawals"
                        icon={TrendingUp}
                        accent="#5BA4A4"
                        delay={0.1}
                        testId="portal-trading-btn"
                        onClick={() => navigate(tradingPath)}
                        bullets={[
                            { icon: Bot, text: 'CyberAI trading bot with performance tiers' },
                            { icon: TrendingUp, text: 'Real-time market data & portfolio analytics' },
                            { icon: ShieldCheck, text: 'Multi-network deposits and secure withdrawals' },
                        ]}
                    />
                    <PortalCard
                        label="Portal 02"
                        title="Recovery Wallet"
                        subtitle={config.tagline}
                        icon={LifeBuoy}
                        accent={accent}
                        delay={0.25}
                        testId="portal-recovery-btn"
                        onClick={() => navigate(recoveryPath)}
                        bullets={[
                            { icon: Radar, text: 'Blockchain forensics & stolen-fund tracing' },
                            { icon: LifeBuoy, text: 'Live case tracker from report to release' },
                            { icon: Scale, text: 'Exchange freezes, legal & key recovery methods' },
                        ]}
                    />
                </div>

                <p className="text-xs text-gray-600 mt-8">
                    {mode === 'login' ? "Don't have an account? " : 'Already registered? '}
                    <Link to={`/portal?mode=${mode === 'login' ? 'register' : 'login'}`} className="text-[#5BA4A4] hover:underline" data-testid="portal-switch-mode">
                        {mode === 'login' ? 'Create one' : 'Sign in instead'}
                    </Link>
                </p>
            </main>
        </div>
    );
}
